import Popup from '@/components/Popup';
import { useAnnouncementBarPopup } from '@/hooks/homepage';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

interface Props {
  open: boolean;
  closePopup: () => void;
  announcementBarId: string;
  itemId: string;
}

export default function AnnouncementBarPopup({ open, closePopup, announcementBarId, itemId }: Readonly<Props>) {
  const router = useRouter();
  const { data: popup, isLoading, isError } = useAnnouncementBarPopup(announcementBarId, itemId);

  const imageUrl = popup?.ImageUrl;
  const redirectUrl = popup?.RedirectUrl;
  const hasNoContent = !isLoading && (isError || !popup || (!imageUrl && !redirectUrl));
  const shouldRedirect = !isLoading && !isError && !imageUrl && !!redirectUrl;

  useEffect(() => {
    if (!open || isLoading) return;

    if (shouldRedirect && redirectUrl) {
      router.push(redirectUrl);
      closePopup();
    } else if (hasNoContent) {
      closePopup();
    }
  }, [open, isLoading, shouldRedirect, redirectUrl, hasNoContent, closePopup, router]);

  if (!open || hasNoContent || shouldRedirect) return null;

  const popupImage = imageUrl ? (
    <Image
      src={imageUrl}
      alt="Popup"
      width={0}
      height={0}
      sizes="100vw"
      className="block h-auto max-h-[calc(var(--popup-size)*1vh)] w-full !max-w-[calc(var(--popup-size)*1vw)]"
      data-testid="popup-image"
    />
  ) : null;

  const onImageClick = () => {
    if (redirectUrl) {
      router.push(redirectUrl);
      closePopup();
    }
  };

  let content = null;
  if (isLoading) {
    content = <div className="h-full w-full animate-pulse bg-gray-200" data-testid="announcement-bar-popup-skeleton" />;
  } else if (imageUrl && redirectUrl) {
    content = (
      <button type="button" className="block h-auto w-full border-0 bg-transparent p-0 active:scale-100" onClick={onImageClick}>
        {popupImage}
      </button>
    );
  } else if (imageUrl) {
    content = popupImage;
  }

  return (
    <Popup open={open} closePopup={closePopup}>
      <div className="bg-white">{content}</div>
    </Popup>
  );
}
